/**
 * Phase 7.1 — Mobile app-shell (top app bar + bottom tab bar + overflow menu).
 *
 * Global chrome that replaces the desktop sidebar on viewports < 1024px
 * (the app's existing `lg:` breakpoint). Reuses all existing routes,
 * gating logic (`useAuth().can`), and handlers (Sync via /sync/now,
 * Logout via useAuth().logout). Zero backend / routing / testid changes.
 *
 * Layout structure:
 *   • MobileTopBar    — sticky, ~56px + safe-area, studio brand + title +
 *                       profile avatar. Tapping avatar opens overflow menu.
 *   • MobileTabBar    — fixed to bottom, 5 core routes with pastel icon
 *                       tiles + labels; active tile = champagne-gold fill.
 *   • OverflowMenu    — dropdown from top-right, dark scrim behind.
 *                       Studio header, SYNC NOW, TRIAL badge (via PlanBadge),
 *                       MORE routes (gate-filtered), Logout.
 */
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Briefcase, Film, IndianRupee,
  RefreshCw, LogOut, ChevronRight, BookOpen, CalendarClock,
  Upload as UploadIcon, Settings as SettingsIcon, Trash2, FileSearch,
  HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api, API_BASE } from "@/lib/api";
import { logoUrl as buildLogoUrl } from "@/lib/brandKit";
import { fetchBillingUsage, planTone, trialDaysLeft } from "@/lib/billing";

// ---------------------------------------------------------------------------
// Bottom-tab items — the 5 core surfaces. Order and labels mirror the
// desktop sidebar's first five entries exactly. The remaining 6 destinations
// live in the overflow menu.
const TAB_ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, to: "/dashboard",
    tileBg: "var(--ds-tile-dashboard-bg)", tileInk: "var(--ds-tile-dashboard-ink)", gate: "studio_member" },
  { key: "leads",     label: "Pipeline",  icon: Users,           to: "/leads",
    tileBg: "var(--ds-tile-pipeline-bg)",  tileInk: "var(--ds-tile-pipeline-ink)",  gate: "studio_member" },
  { key: "projects",  label: "Projects",  icon: Briefcase,       to: "/projects",
    tileBg: "var(--ds-tile-projects-bg)",  tileInk: "var(--ds-tile-projects-ink)",  gate: "projects_read" },
  { key: "delivery",  label: "Delivery",  icon: Film,            to: "/delivery",
    tileBg: "var(--ds-tile-delivery-bg)",  tileInk: "var(--ds-tile-delivery-ink)",  gate: "delivery_read" },
  { key: "accounts",  label: "Accounts",  icon: IndianRupee,     to: "/accounts",
    tileBg: "var(--ds-tile-accounts-bg)",  tileInk: "var(--ds-tile-accounts-ink)",  gate: "payments_write" },
];

// Overflow "MORE" items — the 6 destinations that don't fit in the bottom
// tab bar. Each carries the pastel icon-tile palette used in the drawer
// (matches the desktop sidebar's per-item tile palette 1:1). Gate-filtered
// per role, same gates as the desktop sidebar.
const MORE_ITEMS = [
  { key: "catalog",   label: "Catalog",    to: "/catalog",              gate: "nav_catalog",
    icon: BookOpen,      tileBg: "var(--ds-tile-catalog-bg)",  tileInk: "var(--ds-tile-catalog-ink)" },
  { key: "today",     label: "Follow-ups", to: "/today",                gate: "studio_member",
    icon: CalendarClock, tileBg: "var(--ds-tile-today-bg)",    tileInk: "var(--ds-tile-today-ink)" },
  { key: "import",    label: "Import",     to: "/import",               gate: "import_use",
    icon: UploadIcon,    tileBg: "var(--ds-tile-import-bg)",   tileInk: "var(--ds-tile-import-ink)" },
  { key: "settings",  label: "Settings",   to: "/settings",             gate: "team_manage",
    icon: SettingsIcon,  tileBg: "var(--ds-tile-settings-bg)", tileInk: "var(--ds-tile-settings-ink)" },
  { key: "trash",     label: "Trash",      to: "/trash",                gate: "team_manage",
    icon: Trash2,        tileBg: "var(--ds-tile-trash-bg)",    tileInk: "var(--ds-tile-trash-ink)" },
  { key: "audit-log", label: "Audit Log",  to: "/settings/audit-log",   gate: "audit_read_finance",
    icon: FileSearch,    tileBg: "var(--ds-tile-audit-bg)",    tileInk: "var(--ds-tile-audit-ink)" },
  { key: "help",      label: "Help",       to: "/help",                 gate: "studio_member",
    icon: HelpCircle,    tileBg: "var(--ds-tile-help-bg)",     tileInk: "var(--ds-tile-help-ink)" },
];

// Screen title map — derives the centered top-bar title from the current
// route. Falls back to "SnapOps" when nothing matches (e.g. deep routes).
const TITLE_MAP = [
  { match: (p) => p === "/dashboard",                    title: "Dashboard" },
  { match: (p) => p === "/leads" || p.startsWith("/leads/"),           title: "Pipeline" },
  { match: (p) => p === "/projects" || p.startsWith("/projects/"),     title: "Projects" },
  { match: (p) => p === "/catalog" || p.startsWith("/vendors/") || p.startsWith("/editors/"), title: "Catalog" },
  { match: (p) => p === "/delivery" || p.startsWith("/delivery/"),     title: "Delivery" },
  { match: (p) => p === "/accounts",                     title: "Accounts" },
  { match: (p) => p === "/today",                        title: "Follow-ups" },
  { match: (p) => p === "/import",                       title: "Import" },
  { match: (p) => p === "/settings/audit-log",           title: "Audit Log" },
  { match: (p) => p === "/settings",                     title: "Settings" },
  { match: (p) => p === "/trash",                        title: "Trash" },
  { match: (p) => p === "/help",                         title: "Help" },
  { match: (p) => p === "/my-work",                      title: "My Work" },
];
function screenTitle(pathname) {
  const hit = TITLE_MAP.find((row) => row.match(pathname));
  return hit ? hit.title : "SnapOps";
}

// ---------------------------------------------------------------------------
// Compact plan/trial pill for the mobile drawer workspace chip. Fetches
// live billing usage once (same source PlanBadge uses on desktop) and
// renders a right-aligned pill matching the mockup: gold dot + short label
// (e.g. "TRIAL · 10d left" / "GRANDFATHERED" / "STUDIO"). Tap → /settings.
function MobilePlanPill() {
  const [usage, setUsage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await fetchBillingUsage();
      if (!cancelled) setUsage(u);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!usage) return null;
  const tone = planTone(usage.plan);
  const days = trialDaysLeft(usage);
  const label = usage.plan === "trial" && days !== null
    ? `TRIAL · ${days}d left`
    : (usage.label || "").toUpperCase();

  return (
    <button
      data-testid="mobile-menu-plan-badge"
      onClick={() => navigate("/settings")}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.02em",
        color: tone.fg || "#95643C",
        background: tone.bg || "#FBEFE0",
        border: "1px solid rgba(176,141,85,0.25)",
        padding: "7px 11px",
        borderRadius: 999,
        cursor: "pointer",
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: "var(--ds-accent)",
        }}
      />
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
export default function MobileShell({ onLogout }) {
  const { user, studio, can } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close menu on route change — a tap on a "MORE" item navigates, and the
  // menu should collapse behind the incoming screen.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const title = screenTitle(location.pathname);
  const studioName = studio?.name || "Nuptial Diaries";
  const roleLabel = (user?.role || "owner").toUpperCase();
  const initial = (user?.name || user?.email || "?").slice(0, 1).toUpperCase();
  const logoSrc = buildLogoUrl(studio);
  const logoFullUrl = logoSrc ? `${API_BASE.replace(/\/api$/, "")}${logoSrc}` : null;

  const visibleTabs = useMemo(() => TAB_ITEMS.filter((i) => can(i.gate)), [can]);
  const plan = (studio?.plan || "free").toLowerCase();
  const visibleMore = useMemo(
    () => MORE_ITEMS.filter((i) => can(i.gate) && !(i.key === "help" && plan === "free")),
    [can, plan],
  );

  const handleSync = async () => {
    setMenuOpen(false);
    try {
      const { data } = await api.post("/sync/now");
      const c = data?.counts || {};
      toast.success(`Sync: +${c.inserted ?? 0} · refreshed ${c.refreshed ?? 0}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    }
  };

  return (
    <>
      {/* ═══ Top app bar ═══════════════════════════════════════════════ */}
      <header
        data-testid="mobile-top-bar"
        className="lg:hidden"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 30,
          background: "rgba(247,243,236,0.9)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(34,56,44,0.07)",
          paddingTop: "env(safe-area-inset-top)",
        }}
      >
        <div
          className="flex items-center justify-between relative"
          style={{ height: 56, padding: "0 16px" }}
        >
          {/* Left — studio logo tile + wordmark */}
          <div
            className="flex items-center gap-2 min-w-0"
            data-testid="mobile-top-brand"
            style={{ flex: "1 1 0", minWidth: 0, maxWidth: "calc(50% - 60px)" }}
          >
            {logoFullUrl ? (
              <img src={logoFullUrl} alt={studioName} style={{ height: 30, borderRadius: 8 }} />
            ) : (
              <div
                aria-hidden="true"
                style={{
                  width: 30, height: 30,
                  borderRadius: 9,
                  background: "var(--ds-ink)",
                  display: "grid", placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    width: 11, height: 11,
                    background: "var(--ds-accent)",
                    transform: "rotate(45deg)",
                    borderRadius: 2,
                    display: "inline-block",
                  }}
                />
              </div>
            )}
            <span
              className="truncate"
              style={{
                fontFamily: "var(--ds-num-font)",
                fontSize: 18,
                fontWeight: 500,
                color: "var(--ds-ink)",
                letterSpacing: "0.01em",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {studioName}
            </span>
          </div>

          {/* Center — current screen title (absolutely centered) */}
          <span
            data-testid="mobile-top-title"
            style={{
              fontFamily: "var(--ds-num-font)",
              fontSize: 16,
              color: "var(--ds-ink)",
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              pointerEvents: "none",
              maxWidth: "50%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </span>

          {/* Right — profile avatar (opens overflow menu) */}
          <button
            data-testid="mobile-profile-button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Open profile menu"
            aria-expanded={menuOpen}
            style={{
              width: 38, height: 38,
              borderRadius: "50%",
              background: "linear-gradient(135deg,#C9A46B,#A07C46)",
              color: "#F7F3EC",
              display: "grid", placeItems: "center",
              fontFamily: "var(--ds-num-font)",
              fontSize: 14, fontWeight: 600,
              border: "none",
              cursor: "pointer",
              boxShadow: menuOpen
                ? "0 0 0 3px rgba(176,141,85,0.35), 0 2px 8px rgba(176,141,85,0.4)"
                : "0 2px 8px rgba(176,141,85,0.4)",
              flexShrink: 0,
              transition: "box-shadow .18s ease",
            }}
          >
            {initial}
          </button>
        </div>
      </header>

      {/* ═══ Overflow menu (scrim + dropdown) ══════════════════════════ */}
      {menuOpen && (
        <>
          <div
            data-testid="mobile-menu-scrim"
            onClick={() => setMenuOpen(false)}
            className="lg:hidden"
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 40,
              background: "rgba(20,30,24,0.34)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
            }}
          />
          <div
            data-testid="mobile-overflow-menu"
            className="lg:hidden"
            style={{
              position: "fixed",
              top: `calc(env(safe-area-inset-top) + 62px)`,
              right: 12,
              zIndex: 50,
              width: 296,
              background: "#F7F3EC",
              borderRadius: 22,
              boxShadow: "0 24px 50px -10px rgba(20,30,24,0.4), 0 0 0 1px rgba(34,56,44,0.05)",
              maxHeight: "82vh",
              overflowY: "auto",
              padding: "16px 14px 14px",
            }}
          >
            {/* Workspace chip — studio name + role on left, plan/trial pill on right */}
            <div
              style={{
                background: "#FFFDFA",
                border: "1px solid rgba(34,56,44,0.10)",
                borderRadius: 18,
                padding: 14,
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  data-testid="mobile-menu-studio-name"
                  style={{
                    fontFamily: "var(--ds-num-font)",
                    fontSize: 17,
                    fontWeight: 500,
                    color: "var(--ds-ink)",
                    marginBottom: 5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {studioName}
                </div>
                <span
                  data-testid="mobile-menu-role"
                  style={{
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing: "0.12em",
                    color: "var(--ds-accent)",
                    background: "rgba(176,141,85,0.14)",
                    padding: "3px 9px",
                    borderRadius: 999,
                  }}
                >
                  {roleLabel}
                </span>
              </div>
              {/* Plan/trial pill — mirrors the desktop PlanBadge data (plan label
                  + trial-days-left for trial studios) but as a compact right-aligned
                  pill matching the mockup. Navigates to /settings on tap. */}
              <MobilePlanPill />
            </div>

            {/* Sync now — plain row before MANAGE section */}
            <button
              data-testid="mobile-menu-sync"
              onClick={handleSync}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 11,
                padding: "10px 6px",
                marginBottom: 14,
                borderRadius: 11,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                minHeight: 44,
              }}
            >
              <RefreshCw size={16} strokeWidth={1.9} color="var(--ds-ink)" />
              <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ds-ink)" }}>
                Sync now
              </span>
            </button>

            {/* MANAGE — grouped card of icon-tile rows */}
            {visibleMore.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: "0.14em",
                    color: "var(--ds-accent)",
                    padding: "0 2px 10px",
                  }}
                >
                  MANAGE
                </div>
                <div
                  style={{
                    background: "#FFFDFA",
                    border: "1px solid rgba(34,56,44,0.10)",
                    borderRadius: 18,
                    overflow: "hidden",
                    marginBottom: 10,
                  }}
                >
                  {visibleMore.map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.key}
                        to={item.to}
                        data-testid={`mobile-menu-${item.key}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 13,
                          padding: "12px 14px",
                          minHeight: 60,
                          textDecoration: "none",
                          borderTop: i > 0 ? "1px solid rgba(34,56,44,0.07)" : "none",
                        }}
                      >
                        <div
                          aria-hidden="true"
                          style={{
                            width: 42, height: 42,
                            flexShrink: 0,
                            borderRadius: 12,
                            background: item.tileBg,
                            display: "grid",
                            placeItems: "center",
                            color: item.tileInk,
                          }}
                        >
                          <Icon size={19} strokeWidth={1.9} />
                        </div>
                        <span
                          style={{
                            flex: 1,
                            fontSize: 15,
                            fontWeight: 600,
                            color: "var(--ds-ink)",
                          }}
                        >
                          {item.label}
                        </span>
                        <ChevronRight size={17} color="rgba(34,56,44,0.28)" />
                      </NavLink>
                    );
                  })}
                </div>
              </>
            )}

            {/* Logout — outside the card, red text */}
            <button
              data-testid="mobile-menu-logout"
              onClick={() => { setMenuOpen(false); onLogout && onLogout(); }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 11,
                padding: "12px 6px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                minHeight: 44,
              }}
            >
              <LogOut size={17} strokeWidth={1.9} color="#A24B3B" />
              <span style={{ fontSize: 14, fontWeight: 700, color: "#A24B3B" }}>Logout</span>
            </button>
          </div>
        </>
      )}

      {/* ═══ Bottom tab bar ════════════════════════════════════════════ */}
      <nav
        data-testid="mobile-tab-bar"
        className="lg:hidden"
        style={{
          position: "fixed",
          left: 0, right: 0, bottom: 0,
          // NB: must beat the platform "Made with Emergent" badge (z=9999,
          // fixed bottom-right, 178×40) which otherwise sits inside our tab
          // bar's box and intercepts taps on the Delivery + Accounts tiles.
          zIndex: 10000,
          background: "rgba(247,243,236,0.94)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderTop: "1px solid rgba(34,56,44,0.07)",
          padding: "8px 8px calc(env(safe-area-inset-bottom, 0px) + 12px)",
        }}
      >
        <div className="flex justify-around items-start">
          {visibleTabs.map((item) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.to ||
              location.pathname.startsWith(item.to + "/");
            return (
              <NavLink
                key={item.key}
                to={item.to}
                data-testid={`mobile-tab-${item.key}`}
                aria-current={isActive ? "page" : undefined}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                  flex: 1,
                  textDecoration: "none",
                  minWidth: 0,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 44, height: 34,
                    borderRadius: 12,
                    display: "grid", placeItems: "center",
                    background: isActive
                      ? "linear-gradient(150deg,#C8A063,#A87C42)"
                      : item.tileBg,
                    color: isActive ? "#F7F3EC" : item.tileInk,
                    boxShadow: isActive ? "0 4px 12px rgba(176,141,85,0.4)" : "none",
                    transition: "background .18s ease, box-shadow .18s ease",
                  }}
                >
                  <Icon size={19} strokeWidth={isActive ? 2 : 1.9} />
                </span>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: isActive ? 700 : 600,
                    color: isActive ? "var(--ds-ink)" : "rgba(34,56,44,0.55)",
                    lineHeight: 1,
                  }}
                >
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </>
  );
}
